import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

const MIN_WIDTH = 300;
const MIN_HEIGHT = 220;

export interface ResizableTerminal {
  width: number | null;
  height: number | null;
  maximized: boolean;
  minimized: boolean;
  dragging: boolean;
  startResize: (e: React.PointerEvent) => void;
  toggleMaximize: () => void;
  toggleMinimize: () => void;
  reset: () => void;
}

/**
 * גודל טרמינל ניתן לשליטה — כפתורי חלון רטרו (מזער/הגדל למסך מלא/אפס) + גרירת
 * פינה, בהשראת חלון ה-Program Console ב-pranx.com/hacker. עובד גם במגע (iPad)
 * וגם בעכבר דרך Pointer Events, בלי להסתמך על CSS resize (תמיכה חלשה במגע).
 */
export function useResizableTerminal(containerRef: React.RefObject<HTMLElement | null>): ResizableTerminal {
  const [width, setWidth] = useState<number | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [maximized, setMaximized] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const animRef = useRef<Animation | null>(null);

  /**
   * מבצע שינוי מצב (איפוס/מזעור/מסך מלא) ומריץ אנימציית FLIP מהמידות והמיקום הקודמים לחדשים —
   * החלון זז ומשנה גודל בחלקות במקום לקפוץ. בלי תלות בגודל החדש מראש, כך שעובד גם כשהמצב החדש
   * מוגדר ב-CSS (מסך מלא) או כשהגובה נקבע לפי התוכן (איפוס). מכבד prefers-reduced-motion.
   */
  const animateChange = useCallback(
    (change: () => void) => {
      const el = containerRef.current;
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      if (!el || reduceMotion || typeof el.animate !== 'function') {
        change();
        return;
      }
      const first = el.getBoundingClientRect(); // כולל אנימציה שרצה כרגע — ממשיכים ממנה בלי קפיצה
      animRef.current?.cancel();
      flushSync(change);
      const last = el.getBoundingClientRect();
      if (last.width === 0 || last.height === 0) return;
      const dx = first.left - last.left;
      const dy = first.top - last.top;
      const sx = first.width / last.width;
      const sy = first.height / last.height;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(sx - 1) < 0.01 && Math.abs(sy - 1) < 0.01) return;
      animRef.current = el.animate(
        [
          { transformOrigin: 'top left', transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` },
          { transformOrigin: 'top left', transform: 'none' },
        ],
        { duration: 340, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
      );
    },
    [containerRef],
  );

  const startResize = useCallback(
    (e: React.PointerEvent) => {
      if (maximized) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      startRef.current = { x: e.clientX, y: e.clientY, w: rect.width, h: rect.height };
      setDragging(true);
      e.preventDefault();
    },
    [containerRef, maximized],
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => {
      const start = startRef.current;
      if (!start) return;
      // RTL: הידית בפינה השמאלית-תחתונה, ולכן גרירה שמאלה מגדילה רוחב
      const dx = start.x - e.clientX;
      const dy = e.clientY - start.y;
      const maxW = window.innerWidth * 0.94;
      const maxH = window.innerHeight * 0.85;
      setWidth(Math.min(maxW, Math.max(MIN_WIDTH, start.w + dx)));
      setHeight(Math.min(maxH, Math.max(MIN_HEIGHT, start.h + dy)));
    };
    const onUp = () => {
      setDragging(false);
      startRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging]);

  const toggleMaximize = useCallback(() => {
    animateChange(() => {
      setMinimized(false);
      setMaximized((m) => !m);
    });
  }, [animateChange]);

  const toggleMinimize = useCallback(() => {
    animateChange(() => {
      setMaximized(false);
      setMinimized((m) => !m);
    });
  }, [animateChange]);

  const reset = useCallback(() => {
    // כבר במצב ברירת המחדל — אין מה לאפס, ולא מרנדרים שוב סתם
    if (width === null && height === null && !maximized && !minimized) return;
    animateChange(() => {
      setWidth(null);
      setHeight(null);
      setMaximized(false);
      setMinimized(false);
    });
  }, [animateChange, width, height, maximized, minimized]);

  return { width, height, maximized, minimized, dragging, startResize, toggleMaximize, toggleMinimize, reset };
}
